import React, {
  type ComponentProps,
  forwardRef,
  type PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { Carousel, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderIntroCarousel = forwardRef<GroupOrderIntroCarouselRef, Props>(
  (props, ref) => {
    const { children: slides, onActiveSlideIndexChange } = props;

    const { formatMessage } = useIntl();

    // ─── State ───────────────────────────────────────────────────────────

    const [_, setActiveSlide] = useState(0);

    // ─── Refs ────────────────────────────────────────────────────────────

    const carouselRef = useRef<CarouselRef>(undefined);

    useImperativeHandle(ref, () => ({
      goToNextSlide: () => carouselRef.current?.goToNextSlide(),
      goToPrevSlide: () => carouselRef.current?.goToPrevSlide(),
    }));

    // ─── Helpers ─────────────────────────────────────────────────────────

    const onActiveSlideChange = useCallback(
      (slideIndex: number) => {
        setActiveSlide(slideIndex);
        onActiveSlideIndexChange?.(slideIndex);
      },
      [onActiveSlideIndexChange],
    );

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View style={styles.container}>
        <Carousel
          carouselRef={carouselRef}
          navControlAccessibilityLabel={formatMessage(messages.goToSlideA11yLabel)}
          onActiveSlideChange={onActiveSlideChange}
          colors={CAROUSEL_COLORS}
          transitionEffect="none"
          hideDots
        >
          {slides}
        </Carousel>
      </View>
    );
  },
);

// ─── Constants ───────────────────────────────────────────────────────────────

const CAROUSEL_COLORS: CarouselColors = {
  active: {
    idle: theme.colors.WHITE,
    hover: theme.colors.CREAM,
  },
  inactive: {
    idle: theme.colors.OPACITY.OATMEAL.DARKER,
    hover: theme.colors.CREAM,
  },
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  goToSlideA11yLabel: {
    defaultMessage: 'Go to slide',
    description: 'Group Order Intro | Carousel | Go to slide a11y label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    marginBottom: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  onActiveSlideIndexChange?: (activeSlideIndex: number) => void;
}>;

export type GroupOrderIntroCarouselRef = {
  goToNextSlide: () => void;
  goToPrevSlide: () => void;
};

type CarouselColors = NonNullable<ComponentProps<typeof Carousel>['colors']>;
type CarouselRef = NonNullable<ComponentProps<typeof Carousel>['carouselRef']>['current'];
